import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { IconButton } from '@mui/material';
import { StyledContainer } from './StyledContainer';
import { StyledCounter } from './StyledCounter';
import { StyledPauseButton } from './StyledPauseButton';
import { StyledPlayButton } from './StyledPlayButton';
import { StyledSlider } from './StyledSlider';

function AudioPlayer({ src }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);

  const audioPlayer = useRef();
  const progressBar = useRef();
  const animationRef = useRef();

  useEffect(() => {
    if (Number.isNaN(audioPlayer.current.duration)) {
      setDuration(0);
    } else {
      setDuration(audioPlayer.current.duration);
    }
  }, [audioPlayer?.current?.loadedmetadata, audioPlayer?.current?.readyState]);

  const calculateTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const whilePlaying = useCallback(() => {
    if (audioPlayer) {
      progressBar.current.value = audioPlayer.current.currentTime;
      setPosition(progressBar.current.value);
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    const prevValue = isPlaying;
    setIsPlaying(!prevValue);
    if (!prevValue) {
      audioPlayer.current.play();
      animationRef.current = requestAnimationFrame(whilePlaying);
    } else {
      audioPlayer.current.pause();
      cancelAnimationFrame(animationRef.current);
    }
  }, [isPlaying, whilePlaying]);

  // go back to start when reached the end
  useEffect(() => {
    if (Number.isNaN(position)) {
      setPosition(0);
    } else if (position !== 0 && position === duration) {
      togglePlayPause();
      setPosition(0);
      progressBar.current.value = 0;
    }
  }, [position, duration, togglePlayPause]);

  const changeRange = (value) => {
    if (!Number.isNaN(value)) {
      audioPlayer.current.currentTime = value;
      setPosition((audioPlayer.current.currentTime));
    }
  };

  return (
    <StyledContainer>
      <audio ref={audioPlayer} src={src} type="audio/mpeg" />
      <IconButton onClick={togglePlayPause}>
        {!isPlaying ? (
          <StyledPlayButton />
        ) : (
          <StyledPauseButton />
        )}
      </IconButton>
      <StyledSlider
        ref={progressBar}
        value={position}
        min={0}
        step={1}
        max={duration}
        onChange={(_, value) => changeRange(value)}
      />
      <StyledCounter>
        {position ? calculateTime(position) : '0:00'}
        {' '}
        /
        {' '}
        {duration ? calculateTime(duration) : '0:00'}
      </StyledCounter>
    </StyledContainer>
  );
}

export default AudioPlayer;
